import type { ReactElement } from 'react';
import { PinDisplay } from './PinDisplay';
import { NumericKeypad } from './NumericKeypad';
import { usePinInput } from './usePinInput';
import styles from './PinEntryScreen.module.css';

export interface PinEntryScreenProps {
  onSubmit: (pin: string) => void;
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Tela de entrada do PIN de evento. Componente de apresentação puro — toda
 * a lógica de autenticação vive no hook `usePinAuthentication` (HUB-147); o
 * estado do teclado (dígitos, auto-confirmação, teclado físico) vive em
 * `usePinInput` (HUB-235, CA-08/CA-09).
 */
export function PinEntryScreen({
  onSubmit,
  isLoading,
  errorMessage,
}: PinEntryScreenProps): ReactElement {
  const { pin, pinLength, errorVisible, appendDigit, handleBackspace, handleConfirm } =
    usePinInput(onSubmit, isLoading, errorMessage);

  return (
    <section className={styles.container} aria-label="Validação de PIN">
      <div className={styles.card}>
        <h1 className={styles.title}>Digite o PIN de Ativação</h1>
        <p className={styles.subtitle}>Digite o PIN de 6 dígitos fornecido pela agência.</p>

        <PinDisplay pin={pin} length={pinLength} />

        {isLoading && (
          <p className={styles.loadingStatus} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            Validando...
          </p>
        )}

        {errorVisible && errorMessage && (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}

        <NumericKeypad
          disabled={isLoading}
          canConfirm={pin.length === pinLength}
          onDigit={appendDigit}
          onBackspace={handleBackspace}
          onConfirm={handleConfirm}
        />
      </div>
    </section>
  );
}
